import React, { useCallback, useMemo, useState } from 'react';
import { Schemas } from '@tonic/central-specialties-utils';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  Center,
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckIcon,
  Icons,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@tonic/central-specialties-ui-themed';
import { FlatList, HStack, Icon, Text } from '@gluestack-ui/themed';
import { UserCard } from '../../../../components/general/cards/UserCard.tsx';
import { SearchBar } from '../../../../components/general/SearchBar.tsx';
import { useSearchFilter } from '../../../../hooks/useSearchFilter.ts';
import { DisabledUserFormModal } from '../../../../components/general/Form/specific/UserForm/DisabledUserFormModal.tsx';

interface DisabledUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  disabledUsers: Schemas['UserModel'][];
  refetch?: () => void;
  handleDisabledStatus: (selectedIds: string[]) => void;
}

const itemsPerPage = 24;

export const DisabledUsersModal = ({
  isOpen,
  onClose,
  disabledUsers,
  refetch,
  handleDisabledStatus,
}: DisabledUsersModalProps) => {
  const [selectedUser, setSelectedUser] = useState<Schemas['UserModel'] | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState('');
  const searchFilteredUsers = useSearchFilter(disabledUsers, searchValue, [
    'fullName',
    'email',
    'employeeNumber',
  ]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  const totalPages = useMemo(
    () => Math.ceil(searchFilteredUsers.length / itemsPerPage),
    [searchFilteredUsers.length],
  );

  const currentFilteredUsersItems = useMemo(() => {
    const startIndex = page * itemsPerPage;
    return searchFilteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [searchFilteredUsers, page]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // Navigation handlers
  const goToPrevPage = useCallback(() => {
    setPage((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  // TODO: when going back from a selected user, search State remains, but search value doesn't show, make sure it does

  if (selectedUser) {
    return (
      <DisabledUserFormModal
        isOpen={isOpen}
        onClose={onClose}
        goBack={() => setSelectedUser(null)}
        disabledUser={selectedUser}
        onReinstateSuccess={() => {
          refetch && refetch();
          onClose();
        }}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalBackdrop />
      <ModalContent
        borderRadius={0}
        maxHeight="90%"
        maxWidth="90%"
        minWidth={500}
      >
        <ModalHeader
          bg="$primary700"
          alignItems="center"
          justifyContent="space-between"
          py="$2"
          pl="$10"
          pr="$6"
        >
          <Text color="$white" size="lg">
            Disabled users
          </Text>
          <HStack space="lg" alignItems="center" gap="$6">
            <Center w="$76">
              <SearchBar onChangeText={setSearchValue} />
            </Center>
            <Center>
              <ModalCloseButton
                w="$8"
                justifyContent="center"
                alignItems="center"
              >
                <Icon color="$white" size="xl" as={Icons.X} />
              </ModalCloseButton>
            </Center>
          </HStack>
        </ModalHeader>

        <ModalBody
          w="$full"
          p="$4"
          my="$2"
          flex={1}
          alignItems="space-between"
          justifyContent="space-between"
        >
          <HStack w="$full" flexWrap="wrap">
            <FlatList
              data={currentFilteredUsersItems}
              keyExtractor={(item) => item.id}
              numColumns={3}
              renderItem={({ item: disabledUser }) => {
                const isChecked = selectedIds.includes(disabledUser?.id);

                return (
                  <HStack key={disabledUser?.id} w="$1/3" py="$1.5" px="$2.5">
                    <Checkbox
                      aria-label={`Checkbox to select user ${disabledUser?.fullName} for reinstatement`}
                      isChecked={isChecked}
                      onChange={() => toggleSelect(disabledUser?.id)}
                      value={disabledUser?.id}
                      size="lg"
                      mr="$3"
                    >
                      <CheckboxIndicator mr="$2">
                        <CheckboxIcon as={CheckIcon} />
                      </CheckboxIndicator>
                    </Checkbox>
                    <UserCard
                      user={disabledUser}
                      onPress={() => setSelectedUser(disabledUser)}
                      isSelected={false}
                      flex={1}
                    />
                  </HStack>
                );
              }}
              ListEmptyComponent={() => (
                <Center w="$full" h="$full">
                  <Text size="lg">
                    {searchValue
                      ? 'No results match search'
                      : 'No disabled users to show'}
                  </Text>
                </Center>
              )}
              initialNumToRender={itemsPerPage}
            />
          </HStack>
          <HStack space="md" justifyContent="center" alignItems="center" p="$4">
            <Button
              variant="outline"
              isDisabled={page === 0}
              onPress={goToPrevPage}
            >
              <ButtonIcon as={Icons.ChevronLeft} />
            </Button>

            <Text>
              Page {page + 1} of {totalPages}
              {totalPages > 0 ? ` (${searchFilteredUsers.length} items)` : ''}
            </Text>

            <Button
              variant="outline"
              isDisabled={page >= totalPages - 1}
              onPress={goToNextPage}
            >
              <ButtonIcon as={Icons.ChevronRight} />
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
      {selectedIds.length > 0 && (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          bg="$backgroundElevated"
          py="$3"
          px="$4"
          width={'$1/2'}
          marginHorizontal={'auto'}
        >
          <Button
            w="$full"
            onPress={() => {
              handleDisabledStatus(selectedIds);
              setSelectedIds([]);
            }}
            size="lg"
            isDisabled={selectedIds.length === 0}
          >
            <ButtonIcon as={Icons.Archive} />
            <ButtonText>
              Reinstate User{selectedIds.length > 1 ? 's' : ''}
            </ButtonText>
          </Button>
        </Box>
      )}
    </Modal>
  );
};
